import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronsUpDown, Check, Search } from "lucide-react";
import { studentShortLabel } from "@/lib/utils-app";

// V1.80 · Desplegable único de alumnos: buscador + orden configurable
// (nombre · 1er apellido · 2º apellido). El criterio elegido se recuerda y
// se aplica a todos los desplegables de alumnos de la aplicación.
const SORT_STORAGE_KEY = "naturae.studentPicker.sort";

const txt = (v) => String(v || "").trim();
const norm = (v) =>
  String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export const STUDENT_SORTS = [
  { key: "nombre", label: "Nombre", keyOf: (s) => [txt(s.name), txt(s.primer_apellido), txt(s.segundo_apellido)].filter(Boolean).join(" ") },
  { key: "apellido1", label: "1er apellido", keyOf: (s) => [txt(s.primer_apellido), txt(s.segundo_apellido), txt(s.name)].filter(Boolean).join(" ") },
  { key: "apellido2", label: "2º apellido", keyOf: (s) => [txt(s.segundo_apellido), txt(s.primer_apellido), txt(s.name)].filter(Boolean).join(" ") },
];

export default function StudentPicker({
  value,
  onChange,
  students = [],
  label,
  placeholder = "Selecciona un alumno",
  testId = "student-picker",
  optionTestIdPrefix,
  renderMeta,
  allOption,
  extraSorts = [],
  triggerClassName = "h-11 rounded-xl",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  const sorts = useMemo(() => [...STUDENT_SORTS, ...extraSorts], [extraSorts]);
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem(SORT_STORAGE_KEY) || "nombre"; } catch { return "nombre"; }
  });
  const activeSort = sorts.find((s) => s.key === sortKey) || sorts[0];

  const changeSort = (k) => {
    setSortKey(k);
    try { localStorage.setItem(SORT_STORAGE_KEY, k); } catch { /* noop */ }
  };

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    const t = setTimeout(() => searchRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  const visible = useMemo(() => {
    const q = norm(query);
    const list = q
      ? students.filter((s) =>
          norm(`${s.name} ${s.primer_apellido || ""} ${s.segundo_apellido || ""} ${s.dni || ""} ${s.phone || ""} ${s.codigo_expediente || ""}`).includes(q))
      : [...students];
    if (activeSort?.compare) return list.sort(activeSort.compare);
    return list.sort((a, b) => norm(activeSort.keyOf(a)).localeCompare(norm(activeSort.keyOf(b)), "es"));
  }, [students, query, activeSort]);

  const selected = students.find((s) => s.id === value);
  const triggerLabel = selected
    ? studentShortLabel(selected) || selected.name
    : allOption && value === allOption.value
      ? allOption.label
      : "";

  const prefix = optionTestIdPrefix || testId;

  const pick = (v) => {
    onChange?.(v);
    setOpen(false);
  };

  return (
    <div>
      {label && <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testId}
            className={
              "w-full flex items-center justify-between gap-2 border border-neutral-300 bg-white px-3 text-sm text-left " +
              "focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 " + triggerClassName
            }
          >
            <span className={"truncate " + (triggerLabel ? "font-medium text-neutral-900" : "text-neutral-400")}>
              {triggerLabel || placeholder}
            </span>
            {selected && renderMeta && (
              <span className="text-xs text-neutral-500 font-mono tabular-nums shrink-0">{renderMeta(selected)}</span>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-neutral-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]"
          data-testid={`${testId}-panel`}
        >
          <div className="p-2 border-b border-neutral-100 space-y-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar alumno…"
                data-testid={`${testId}-search`}
                className="w-full rounded-lg border border-neutral-300 bg-white pl-8 pr-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex items-center gap-1 flex-wrap" data-testid={`${testId}-sort`}>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mr-0.5">Ordenar</span>
              {sorts.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => changeSort(s.key)}
                  data-testid={`${testId}-sort-${s.key}`}
                  className={
                    "px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors " +
                    (s.key === activeSort.key
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "bg-white border-neutral-300 text-neutral-600 hover:bg-orange-50")
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {allOption && (
              <button
                type="button"
                onClick={() => pick(allOption.value)}
                data-testid={`${prefix}-option-all`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center gap-2"
              >
                {value === allOption.value && <Check className="h-3.5 w-3.5 text-orange-600" />}
                <span className="font-medium">{allOption.label}</span>
              </button>
            )}
            {visible.length === 0 && (
              <div className="px-3 py-3 text-sm text-neutral-500" data-testid={`${testId}-empty`}>
                Sin resultados
              </div>
            )}
            {visible.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s.id)}
                data-testid={`${prefix}-option-${s.id}`}
                className={
                  "w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center gap-2 " +
                  (s.id === value ? "bg-orange-50" : "")
                }
              >
                {s.id === value && <Check className="h-3.5 w-3.5 text-orange-600 shrink-0" />}
                <span className="font-medium truncate flex-1 min-w-0">{studentShortLabel(s) || s.name}</span>
                {renderMeta && (
                  <span className="text-xs text-neutral-500 font-mono tabular-nums shrink-0">{renderMeta(s)}</span>
                )}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
